import React from 'react';
import {
  Typography,
  Stack,
  Box,
  Button,
  Paper,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import moment from 'moment';

import {
  ExternalLink,
  CheckCircle,
  AlertTriangle,
  XCircle,
} from 'react-feather';

import { InfoRow } from './components/InfoRow';
import { PaymentsTable } from './components/PaymentsTable';

interface Venue {
  id: string;
  name: string;
  defaultPaymentProvider: string;
  achEnabled: boolean;
  paymentsEnabled: boolean;
  squareLocationId?: string;
  squareAccount?: {
    id: string;
    squareMerchantId: string;
    createdAt: string;
  };
  stripeAccount?: {
    id: string;
    stripeAccountId: string;
    createdAt: string;
  };
}

const formatDate = (dateString: string) => {
  try {
    return moment(new Date(dateString)).format('MMM D, YYYY');
  } catch {
    return 'Invalid date';
  }
};

const getPaymentStatus = (venue: Venue) => {
  if (!venue.paymentsEnabled) {
    return {
      status: 'error' as const,
      message: 'Payments are disabled for this venue',
    };
  }

  const hasStripe = !!venue.stripeAccount;
  const hasSquare = !!venue.squareAccount;
  const hasSquareLocation = !!venue.squareLocationId;

  if (venue.defaultPaymentProvider === 'stripe' && hasStripe) {
    return {
      status: 'success' as const,
      message: 'Stripe is connected and active',
    };
  }

  if (
    venue.defaultPaymentProvider === 'square' &&
    hasSquare &&
    hasSquareLocation
  ) {
    return {
      status: 'success' as const,
      message: 'Square is connected and active',
    };
  }

  if (
    venue.defaultPaymentProvider === 'square' &&
    hasSquare &&
    !hasSquareLocation
  ) {
    return {
      status: 'warning' as const,
      message: 'Square account connected but missing location ID',
    };
  }

  if (venue.defaultPaymentProvider === 'stripe' && !hasStripe) {
    return {
      status: 'warning' as const,
      message: 'Payment provider is Stripe but no account connected',
    };
  }

  if (venue.defaultPaymentProvider === 'square' && !hasSquare) {
    return {
      status: 'warning' as const,
      message: 'Payment provider is Square but no account connected',
    };
  }

  return {
    status: 'warning' as const,
    message: 'Payment configuration may be incomplete',
  };
};

export const PaymentsPanel = ({ venue }: { venue: Venue }) => {
  const paymentStatus = getPaymentStatus(venue);

  return (
    <Box p={3}>
      <Stack spacing={3}>
        <Typography variant="h5" gutterBottom>
          Payment Processor
        </Typography>

        {/* Overall Status Alert */}
        <Alert
          severity={
            paymentStatus.status === 'success'
              ? 'success'
              : paymentStatus.status === 'warning'
              ? 'warning'
              : 'error'
          }
          icon={
            paymentStatus.status === 'success' ? (
              <CheckCircle />
            ) : paymentStatus.status === 'warning' ? (
              <AlertTriangle />
            ) : (
              <XCircle />
            )
          }
        >
          <Typography variant="body2">{paymentStatus.message}</Typography>
        </Alert>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {/* General Payment Settings */}
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              General Settings
            </Typography>
            <InfoRow
              label="Payments Enabled"
              value={venue.paymentsEnabled ? 'Yes' : 'No'}
              status={venue.paymentsEnabled ? 'success' : 'error'}
            />
            <InfoRow
              label="Default Payment Provider"
              value={
                venue.defaultPaymentProvider.charAt(0).toUpperCase() +
                venue.defaultPaymentProvider.slice(1)
              }
            />
            <InfoRow
              label="ACH Enabled"
              value={venue.achEnabled ? 'Yes' : 'No'}
              status={venue.achEnabled ? 'success' : undefined}
            />
          </Paper>

          {/* Stripe Account Details */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              flex: 1,
              position: 'relative',
              borderWidth: venue.defaultPaymentProvider === 'stripe' ? 2 : 1,
              borderColor:
                venue.defaultPaymentProvider === 'stripe'
                  ? 'primary.main'
                  : 'divider',
              backgroundColor:
                venue.defaultPaymentProvider === 'stripe'
                  ? 'primary.50'
                  : 'background.paper',
              '&::before':
                venue.defaultPaymentProvider === 'stripe'
                  ? {
                      content: '""',
                      position: 'absolute',
                      top: -1,
                      left: -1,
                      right: -1,
                      height: 4,
                      backgroundColor: 'primary.main',
                      borderRadius: '4px 4px 0 0',
                    }
                  : {},
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">Stripe Account</Typography>
              {venue.defaultPaymentProvider === 'stripe' && (
                <Chip
                  label="Active"
                  color="primary"
                  size="small"
                  variant="filled"
                />
              )}
            </Stack>
            {venue.stripeAccount ? (
              <Stack spacing={1}>
                <InfoRow label="Status" value="Connected" status="success" />
                <InfoRow
                  label="Account ID"
                  value={venue.stripeAccount.stripeAccountId}
                />
                <InfoRow
                  label="Connected Date"
                  value={formatDate(venue.stripeAccount.createdAt)}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  py={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    Stripe Dashboard:
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ExternalLink size={16} />}
                    href={`https://dashboard.stripe.com/connect/accounts/${venue.stripeAccount.stripeAccountId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Account
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <InfoRow label="Status" value="Not Connected" status="error" />
                <Typography variant="body2" color="text.secondary">
                  No Stripe account is connected to this venue.
                </Typography>
              </Stack>
            )}
          </Paper>

          {/* Square Account Details */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              flex: 1,
              position: 'relative',
              borderWidth: venue.defaultPaymentProvider === 'square' ? 2 : 1,
              borderColor:
                venue.defaultPaymentProvider === 'square'
                  ? 'primary.main'
                  : 'divider',
              backgroundColor:
                venue.defaultPaymentProvider === 'square'
                  ? 'primary.50'
                  : 'background.paper',
              '&::before':
                venue.defaultPaymentProvider === 'square'
                  ? {
                      content: '""',
                      position: 'absolute',
                      top: -1,
                      left: -1,
                      right: -1,
                      height: 4,
                      backgroundColor: 'primary.main',
                      borderRadius: '4px 4px 0 0',
                    }
                  : {},
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">Square Account</Typography>
              {venue.defaultPaymentProvider === 'square' && (
                <Chip
                  label="Active"
                  color="primary"
                  size="small"
                  variant="filled"
                />
              )}
            </Stack>
            {venue.squareAccount ? (
              <Stack spacing={1}>
                <InfoRow label="Status" value="Connected" status="success" />
                <InfoRow
                  label="Merchant ID"
                  value={venue.squareAccount.squareMerchantId}
                />
                <InfoRow
                  label="Connected Date"
                  value={formatDate(venue.squareAccount.createdAt)}
                />
                <InfoRow
                  label="Location ID"
                  value={venue.squareLocationId || 'Not set'}
                  status={venue.squareLocationId ? 'success' : 'error'}
                />
                {!venue.squareLocationId && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Action Required:</strong> Square account is
                      connected but missing a Location ID. The venue cannot
                      process payments through Square until this is configured.
                    </Typography>
                  </Alert>
                )}
              </Stack>
            ) : (
              <Stack spacing={1}>
                <InfoRow label="Status" value="Not Connected" status="error" />
                <Typography variant="body2" color="text.secondary">
                  No Square account is connected to this venue.
                </Typography>
              </Stack>
            )}
          </Paper>
        </Stack>
        <PaymentsTable venue={venue} />
      </Stack>
    </Box>
  );
};
